import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CircleAlert, ClipboardCheck, ImageOff, Images, MessageSquare } from 'lucide-react';
import {
  Button,
  Callout,
  DropZone,
  Field,
  Input,
  NativeSelect,
  SchnellErfassen,
  Sheet,
  Textarea,
  formatDateigroesse,
  toast,
  type SchnellErfassenOption,
} from '@elbklar/ui';
import { projectsApi } from '@/api/projects';
import { FOTO_MAX_MB, fotoPruefen, fotosApi } from '@/api/fotos';
import { notizenApi } from '@/api/notizen';
import { tasksApi } from '@/api/tasks';
import { useAuth } from '@/features/auth/AuthContext';
import { isApiError } from '@/lib/api';
import type { Project } from '@/types';
import { STATUS_TO_SECTION } from '@/types/workflow';

// Schnell erfassen (Mobil M3): Das „+“ der Tab-Leiste öffnet die Auswahl,
// danach ein kurzes Formular als Sheet. Auf einer Projektseite ist das
// Projekt vorbelegt, sonst wählt man es zuerst. Dieselben Formulare öffnen
// die Karten „Notizen“ und „Fotos“ der Projektseite, am Rechner als Dialog.

export type ErfassenArt = 'foto' | 'notiz' | 'aufgabe';

const OPTIONEN: (SchnellErfassenOption & { id: ErfassenArt })[] = [
  { id: 'foto', label: 'Foto', hint: 'Mit der Kamera, landet im Projekt', icon: Camera },
  { id: 'notiz', label: 'Notiz', hint: 'Zum Projekt, steht auch im CRM beim Kunden', icon: MessageSquare },
  { id: 'aufgabe', label: 'Aufgabe', hint: 'Zum Projekt, steht auch im CRM unter Aufgaben', icon: ClipboardCheck },
];

const TITEL: Record<ErfassenArt, string> = {
  foto: 'Foto hinzufügen',
  notiz: 'Notiz anlegen',
  aufgabe: 'Aufgabe anlegen',
};

interface SchnellErfassenZustand {
  /** Ohne Art die Auswahl, mit Art gleich das Formular. Ein Foto öffnet dabei sofort die Kamera, wenn das Projekt feststeht. */
  oeffnen: (art?: ErfassenArt) => void;
}

const Kontext = createContext<SchnellErfassenZustand | null>(null);

export function useSchnellErfassen() {
  const ctx = useContext(Kontext);
  if (!ctx) throw new Error('useSchnellErfassen nur innerhalb von <SchnellErfassenProvider> verwenden');
  return ctx;
}

/** Fehlertext für das Sheet: die Meldung des Backends (`detail`), sonst ein ruhiger Satz. */
function fehlerText(fehler: unknown, sonst: string): string {
  if (isApiError(fehler)) {
    const detail = (fehler.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (fehler.response?.status === 413) return 'Das Foto ist zu groß.';
    if (!fehler.response) return 'Keine Verbindung zum Server. Erneut versuchen.';
  }
  return sonst;
}

function letzteAktivitaet(p: Project): number {
  return new Date(p.last_activity_at ?? p.updated_at ?? p.created_at).getTime();
}

/**
 * Offene Projekte wie in „Meine Arbeit“ (zugewiesen, nicht abgeschlossen):
 * die eigenen zuerst, darin das zuletzt bearbeitete oben. Wer draußen etwas
 * erfasst, steht meist an dem Projekt, an dem er gerade arbeitet.
 */
function offeneProjekte(liste: Project[], userId: string | undefined): Project[] {
  return liste
    .filter((p) => p.assigned_user_id && STATUS_TO_SECTION[p.status] !== 'erledigt')
    .sort((a, b) => {
      const eigenA = a.assigned_user_id === userId ? 0 : 1;
      const eigenB = b.assigned_user_id === userId ? 0 : 1;
      return eigenA - eigenB || letzteAktivitaet(b) - letzteAktivitaet(a);
    });
}

export function SchnellErfassenProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const kontextId = matchPath({ path: '/projects/:id', end: false }, pathname)?.params.id;

  const [wahlOffen, setWahlOffen] = useState(false);
  const [art, setArt] = useState<ErfassenArt | null>(null);
  const [gewaehlt, setGewaehlt] = useState('');
  const [datei, setDatei] = useState<File | null>(null);
  // Warum die zuletzt gewählte Datei nicht übernommen wurde (kein Foto, zu groß).
  const [dateiFehler, setDateiFehler] = useState<string | null>(null);
  const [vorschauKaputt, setVorschauKaputt] = useState(false);
  const [text, setText] = useState('');
  const [titel, setTitel] = useState('');
  const [faellig, setFaellig] = useState('');
  const kamera = useRef<HTMLInputElement>(null);
  const auswahl = useRef<HTMLInputElement>(null);

  const projektId = kontextId ?? gewaehlt;

  const { data: kontextProjekt } = useQuery({
    queryKey: ['project', kontextId],
    queryFn: () => projectsApi.get(kontextId!),
    enabled: !!kontextId && art !== null,
  });

  // Gleiche Abfrage wie „Meine Arbeit“, der Cache wird geteilt.
  const { data: liste, isLoading: listeLaedt } = useQuery({
    queryKey: ['projects', 'workflow'],
    queryFn: () => projectsApi.list({ per_page: 100 }),
    enabled: !kontextId && art !== null,
  });
  const offene = useMemo(() => offeneProjekte(liste?.items ?? [], user?.id), [liste, user?.id]);

  // Vorschau als Object-URL; beim Wechsel oder Schließen freigeben. Im Effekt
  // angelegt, damit der doppelte Durchlauf im StrictMode sie nicht verwirft.
  const [vorschau, setVorschau] = useState<string | null>(null);
  useEffect(() => {
    if (!datei) {
      setVorschau(null);
      return;
    }
    const url = URL.createObjectURL(datei);
    setVorschau(url);
    return () => URL.revokeObjectURL(url);
  }, [datei]);

  const fotoMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => fotosApi.upload(id, file),
    onSuccess: (_foto, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['fotos', id] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Foto gespeichert');
      setDatei(null);
      setArt(null);
    },
  });

  const notizMutation = useMutation({
    mutationFn: ({ id, inhalt }: { id: string; inhalt: string }) => notizenApi.create(id, inhalt),
    onSuccess: (_notiz, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['notizen', id] });
      toast.success('Notiz gespeichert');
      setText('');
      setArt(null);
    },
  });

  const aufgabeMutation = useMutation({
    mutationFn: ({ id, title, due }: { id: string; title: string; due: string }) =>
      tasksApi.create(id, { title, due_date: due || undefined }),
    onSuccess: (_aufgabe, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id] });
      toast.success('Aufgabe angelegt');
      setTitel('');
      setFaellig('');
      setArt(null);
    },
  });

  const { reset: fotoReset } = fotoMutation;
  const { reset: notizReset } = notizMutation;
  const { reset: aufgabeReset } = aufgabeMutation;
  const formularOeffnen = useCallback(
    (neu: ErfassenArt) => {
      fotoReset();
      notizReset();
      aufgabeReset();
      setVorschauKaputt(false);
      if (neu === 'foto') {
        setDatei(null);
        setDateiFehler(null);
        // Steht das Projekt fest, gleich die Kamera: noch im selben Tipp, sonst
        // blockiert iOS das Öffnen. Das Sheet zeigt danach die Vorschau.
        if (kontextId) kamera.current?.click();
      }
      setArt(neu);
    },
    [kontextId, fotoReset, notizReset, aufgabeReset],
  );

  const oeffnen = useCallback(
    (neu?: ErfassenArt) => {
      if (neu) formularOeffnen(neu);
      else setWahlOffen(true);
    },
    [formularOeffnen],
  );
  const zustand = useMemo(() => ({ oeffnen }), [oeffnen]);

  // „Abbrechen“ verwirft, Wischen und Tippen daneben schließen nur: Eine halb
  // geschriebene Notiz bleibt dann für das nächste Öffnen stehen.
  const abbrechen = () => {
    if (art === 'notiz') setText('');
    if (art === 'aufgabe') {
      setTitel('');
      setFaellig('');
    }
    setDatei(null);
    setDateiFehler(null);
    setArt(null);
  };

  // Kein Foto oder zu groß: gar nicht erst hochladen, sondern gleich sagen,
  // warum. Der Server prüft dasselbe noch einmal (pm-api services/fotos.py).
  const dateiGewaehlt = (neu: File | undefined) => {
    if (!neu) return;
    fotoMutation.reset();
    setVorschauKaputt(false);
    const fehler = fotoPruefen(neu);
    setDateiFehler(fehler);
    setDatei(fehler ? null : neu);
  };

  const ohneProjekt = !projektId;
  const projektName = kontextId ? kontextProjekt?.name : offene.find((p) => p.id === gewaehlt)?.name;
  const beschreibung = kontextId ? (projektName ? `Zu ${projektName}` : undefined) : undefined;

  const projektFeld = !kontextId && (
    <Field label="Projekt" hint={!listeLaedt && offene.length === 0 ? 'Keine offenen Projekte in „Meine Arbeit“.' : undefined}>
      {(f) => (
        <NativeSelect
          {...f}
          placeholder={listeLaedt ? 'Projekte werden geladen …' : 'Projekt wählen'}
          value={gewaehlt}
          onChange={(e) => setGewaehlt(e.target.value)}
          options={offene.map((p) => ({ value: p.id, label: p.customer_name ? `${p.name} · ${p.customer_name}` : p.name }))}
        />
      )}
    </Field>
  );

  const fehler = (meldung: string) => (
    <Callout tone="danger" icon={CircleAlert} title="Nicht gespeichert" boxed>
      {meldung}
    </Callout>
  );

  const abbrechenKnopf = (
    <Button variant="ghost" onClick={abbrechen}>
      Abbrechen
    </Button>
  );

  return (
    <Kontext.Provider value={zustand}>
      {children}

      <SchnellErfassen open={wahlOffen} onOpenChange={setWahlOffen} options={OPTIONEN} onWahl={(id) => formularOeffnen(id as ErfassenArt)} />

      {/* Außerhalb der Sheets, damit „Foto“ die Kamera noch im selben Tipp
          öffnen kann. accept="image/*" statt HEIC ausdrücklich: Dann liefert
          iOS ein JPEG, das jeder Browser anzeigt. Nicht verkleinern, sonst
          ginge das EXIF-GPS verloren. */}
      <input
        ref={kamera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          dateiGewaehlt(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={auswahl}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          dateiGewaehlt(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      <Sheet
        open={art === 'foto'}
        onOpenChange={(o) => !o && setArt(null)}
        title={TITEL.foto}
        description={beschreibung}
        footer={
          <>
            {abbrechenKnopf}
            <Button
              variant="primary"
              disabled={!datei || ohneProjekt}
              loading={fotoMutation.isPending}
              onClick={() => datei && projektId && fotoMutation.mutate({ id: projektId, file: datei })}
            >
              Foto speichern
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {projektFeld}
          {datei ? (
            <figure className="flex flex-col gap-2">
              {vorschau && !vorschauKaputt ? (
                <img src={vorschau} alt="Vorschau des Fotos" onError={() => setVorschauKaputt(true)} className="max-h-[45dvh] w-full rounded-md bg-muted object-contain" />
              ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-md bg-muted text-muted-foreground">
                  <ImageOff size={26} strokeWidth={1.5} aria-hidden />
                  <span className="t-small">Für dieses Format zeigt der Browser keine Vorschau.</span>
                </div>
              )}
              <figcaption className="flex flex-wrap items-center justify-between gap-2 t-small text-muted-foreground">
                <span className="min-w-0 truncate">
                  {datei.name} · {formatDateigroesse(datei.size)}
                </span>
                <Button size="sm" variant="ghost" icon={Camera} onClick={() => kamera.current?.click()}>
                  Foto ersetzen
                </Button>
              </figcaption>
            </figure>
          ) : (
            <>
              <DropZone
                icon={Camera}
                title="Foto aufnehmen oder wählen"
                description={`JPEG, PNG, HEIC oder WebP, bis ${FOTO_MAX_MB} MB. Die Position liest das System aus dem Foto.`}
                onFiles={(files) => dateiGewaehlt(files[0])}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="secondary" icon={Camera} onClick={() => kamera.current?.click()} className="hidden touch:inline-flex">
                  Foto aufnehmen
                </Button>
                <Button variant="secondary" icon={Images} onClick={() => auswahl.current?.click()}>
                  Foto wählen
                </Button>
              </div>
            </>
          )}
          {dateiFehler && (
            <Callout tone="danger" icon={CircleAlert} title="Nicht übernommen" boxed>
              {dateiFehler}
            </Callout>
          )}
          {fotoMutation.isError && fehler(fehlerText(fotoMutation.error, 'Das Foto ließ sich nicht hochladen. Erneut versuchen.'))}
        </div>
      </Sheet>

      <Sheet
        open={art === 'notiz'}
        onOpenChange={(o) => !o && setArt(null)}
        title={TITEL.notiz}
        description={beschreibung}
        footer={
          <>
            {abbrechenKnopf}
            <Button
              variant="primary"
              disabled={!text.trim() || ohneProjekt}
              loading={notizMutation.isPending}
              onClick={() => projektId && notizMutation.mutate({ id: projektId, inhalt: text.trim() })}
            >
              Notiz speichern
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {projektFeld}
          <Field label="Notiz" hint="Steht auch im CRM beim Kunden.">
            {(f) => (
              <Textarea {...f} rows={5} maxLength={10000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Zugang, Besonderheiten, was als Nächstes kommt" />
            )}
          </Field>
          {notizMutation.isError && fehler(fehlerText(notizMutation.error, 'Die Notiz ließ sich nicht speichern. Erneut versuchen.'))}
        </div>
      </Sheet>

      <Sheet
        open={art === 'aufgabe'}
        onOpenChange={(o) => !o && setArt(null)}
        title={TITEL.aufgabe}
        description={beschreibung}
        footer={
          <>
            {abbrechenKnopf}
            <Button
              variant="primary"
              disabled={!titel.trim() || ohneProjekt}
              loading={aufgabeMutation.isPending}
              onClick={() => projektId && aufgabeMutation.mutate({ id: projektId, title: titel.trim(), due: faellig })}
            >
              Aufgabe anlegen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {projektFeld}
          <Field label="Aufgabe">
            {(f) => <Input {...f} maxLength={255} value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Gerüstfirma anrufen" />}
          </Field>
          <Field label="Fällig am" hint="Optional.">
            {(f) => <Input {...f} type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} />}
          </Field>
          {aufgabeMutation.isError && fehler(fehlerText(aufgabeMutation.error, 'Die Aufgabe ließ sich nicht anlegen. Erneut versuchen.'))}
        </div>
      </Sheet>
    </Kontext.Provider>
  );
}
